import React, { useEffect, useMemo, useState } from "react";
import html2pdf from "html2pdf.js";
import { useParams, useNavigate } from "react-router-dom";
import moment from "moment";
import { usePackingList } from "../../queries/packingListQueries";
import { useCustomers } from "../../queries/customerQueries";

// Function to chunk data
const chunkData = (data, chunkSize) => {
  const result = [];
  if (!data) return result;
  for (let i = 0; i < data.length; i += chunkSize) {
    result.push(data.slice(i, i + chunkSize));
  }
  return result;
};

const SwanDownload = ({ pklData }) => {
  const { id } = useParams();
  const navigate = useNavigate();

  // If pklData is passed as a prop, use it; otherwise fetch by ID
  const { data: queryPklData, isLoading, isError, error } = usePackingList(pklData ? null : id);
  const state = pklData || queryPklData;

  // Fetch matching importer customer details by importer name
  const importerName = state?.importer || "";
  const { data: apiCustomers } = useCustomers(1, importerName);

  const importerCustomer = useMemo(() => {
    if (!importerName || !apiCustomers) return null;
    const list = apiCustomers?.data || apiCustomers?.items || (Array.isArray(apiCustomers) ? apiCustomers : []);
    if (!Array.isArray(list)) return null;
    return (
      list.find(
        (c) => c?.customerName?.trim()?.toLowerCase() === importerName.trim()?.toLowerCase()
      ) || list[0] || null
    );
  }, [apiCustomers, importerName]);

  const chunkedData = useMemo(() => {
    return chunkData(state?.newTeakSwans || state?.newTeakSwan || [], 50);
  }, [state]);

  const groupedChunks = useMemo(() => {
    const result = [];
    for (let i = 0; i < chunkedData.length; i += 3) {
      result.push(chunkedData.slice(i, i + 3));
    }
    return result;
  }, [chunkedData]);

  const [summaryPageBreak, setSummaryPageBreak] = useState(false);

  useEffect(() => {
    const checkPageBreak = () => {
      const container = document.getElementById("pdf-content");
      const summary = document.getElementById("summary-section");

      if (container && summary) {
        const containerHeight = container.offsetHeight;
        const summaryTop = summary.offsetTop;
        setSummaryPageBreak(summaryTop > containerHeight - 150);
      }
    };

    setTimeout(checkPageBreak, 500);
  }, []);

  const downloadPDF = () => {
    const element = document.getElementById("pdf-content");

    if (!element) {
      console.error("PDF content element not found!");
      return;
    }

    const options = {
      margin: 5,
      filename: `PKL - ${state && state?.pkl}.pdf`,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 3 },
      jsPDF: { unit: "mm", format: [220, 385], orientation: "portrait" },
    };

    html2pdf()
      .set(options)
      .from(element)
      .toPdf()
      .get("pdf")
      .then((pdf) => {
        const totalPages = pdf.internal.getNumberOfPages();

        for (let i = 1; i <= totalPages; i++) {
          pdf.setPage(i);
          pdf.setFontSize(10);
          pdf.setTextColor(0, 0, 0);

          pdf.text(`Page ${i} `, pdf.internal.pageSize.getWidth() / 2, 7, {
            align: "center",
          });
        }
      })
      .save();
  };

  const Header = () => {
    const importerHeading = (importerCustomer?.customerName || state?.importer || "BEST TIMBERS").toUpperCase();
    const doorNo = importerCustomer?.billingDoorNo;
    const street = importerCustomer?.billingStreet;
    const landmark = importerCustomer?.billingLandmark;
    const city = importerCustomer?.billingCity;
    const district = importerCustomer?.billingDistrict;
    const pincode =
      importerCustomer?.billingPinCode ||
      importerCustomer?.billingPincode ||
      importerCustomer?.pinCode ||
      importerCustomer?.pincode;

    const fromMob = state?.fromNumber || state?.fromMobileNumber || "";
    const fromStreetStr = state?.fromStreet ? state.fromStreet.toUpperCase() : "";
    const fromLandmarkStr = state?.fromLandmark ? state.fromLandmark.toUpperCase() : "";
    const fromCityStr = state?.fromCity ? state.fromCity.toUpperCase() : "";
    const fromDistrictStr = state?.fromDistrict ? state.fromDistrict.toUpperCase() : "";
    const fromPincodeStr = state?.fromPincode || "";

    const rawDate = state?.plDate || state?.date || "";
    const formattedDate = rawDate && moment(rawDate).isValid()
      ? moment(rawDate).format("DD-MM-YYYY")
      : rawDate;

    const hasBillingAddress = Boolean(doorNo || street || city || pincode);

    return (
      <div className="header-section" style={{ marginBottom: "0px" }}>
        <div className="row p-0 m-0">
          <div className="col-6">
            <div className="row">
              <div className="col-12 pkl-download-from-to-address-head p-0 m-0">
                From
              </div>
            </div>
            <div className="row p-0 m-0">
              <div className="col-1"></div>
              <div className="col-11">
                {/* Original hardcoded From section (preserved):
                <h6 className="pkl-download-heading">BEST TIMBERS</h6>
                <p className="pkl-download-from-address">
                  New.NO.515, Old.No. 274,
                </p>
                <p className="pkl-download-from-address">Sydenhams Road,</p>
                <p className="pkl-download-from-address">
                  Nehru Timber Market, Choolai
                </p>
                <p className="pkl-download-from-address">Chennai - 600112</p>
                */}
                <h6 className="pkl-download-heading">{importerHeading}</h6>
                {hasBillingAddress ? (
                  <>
                    {doorNo && <p className="pkl-download-from-address">{doorNo}</p>}
                    {street && (
                      street.includes(",") && street.length > 25 ? (
                        (() => {
                          const commaIndex = street.indexOf(",");
                          const line1 = street.slice(0, commaIndex + 1).trim();
                          const line2 = street.slice(commaIndex + 1).trim();
                          return (
                            <>
                              {line1 && <p className="pkl-download-from-address">{line1}</p>}
                              {line2 && <p className="pkl-download-from-address">{line2}</p>}
                            </>
                          );
                        })()
                      ) : (
                        <p className="pkl-download-from-address">{street}</p>
                      )
                    )}
                    {landmark && <p className="pkl-download-from-address">{landmark}</p>}
                    {(city || pincode) && (
                      <p className="pkl-download-from-address">
                        {`${city || ""}${district ? `, ${district}` : ""}${pincode ? ` - ${pincode}` : ""}`}
                      </p>
                    )}
                  </>
                ) : (
                  <>
                    <p className="pkl-download-from-address">
                      New.NO.515, Old.No. 274,
                    </p>
                    <p className="pkl-download-from-address">Sydenhams Road,</p>
                    <p className="pkl-download-from-address">
                      Nehru Timber Market, Choolai
                    </p>
                    <p className="pkl-download-from-address">Chennai - 600112</p>
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="col-6">
            <div className="row">
              <div
                className="col-8 pkl-download-from-address text-end p-0 m-0"
                style={{ fontStyle: "normal" }}
              >
                <b>DATE : {formattedDate}</b>
              </div>
            </div>
            <div className="row p-0 m-0">
              <div className="col-2 pkl-download-from-to-address-head">To</div>

              <div className="col-10 px-4">
                <p className="pkl-download-address py-1">
                  {state?.fromName?.toUpperCase() || ""}
                </p>
                <p className="pkl-download-address py-1">
                  {`${fromMob}${fromStreetStr}`}
                  ,
                </p>
                <p className="pkl-download-address py-1">
                  {`${fromLandmarkStr}, ${fromCityStr}, `}
                </p>
                <p className="pkl-download-address py-1">
                  {`${fromDistrictStr}-${fromPincodeStr} `}
                </p>
                <p className="pkl-download-address py-1">
                  GST NO : {state?.gstNumber || ""}
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className="row p-0 m-0">
          <div className="col-4 p-0 mt-1 mb-0">
            <i style={{ fontSize: "15" }}>Item : </i>
            <span
              style={{
                fontWeight: "bold",
                fontStyle: "normal",
                fontSize: "17px",
              }}
              className="pkl-download-from-address m-0"
            >
              TEAK SAWN SIZES
            </span>
          </div>
          <div className="col-4 p-0 mt-1 mb-0 text-center">
            <h6
              style={{
                fontWeight: "bold",
                fontStyle: "normal",
                fontSize: "17px",
                margin: 0,
                padding: 0
              }}
              className="pkl-download-from-address"
            >
              PKL-{state?.pkl || ""}
            </h6>
          </div>
          <div className="col-4 p-0 mt-1 mb-0">
            <i style={{ fontSize: "15px" }}>Lorry No : </i>
            <span
              style={{
                fontWeight: "bold",
                fontStyle: "normal",
                fontSize: "17px",
              }}
              className="pkl-download-from-address"
            >
              {state?.lorry || ""}
            </span>
          </div>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="d-flex flex-column align-items-center justify-content-center bg-light" style={{ height: "100vh", width: "100vw" }}>
        <div className="spinner-border text-primary" role="status" style={{ width: "3rem", height: "3rem" }}>
          <span className="visually-hidden">Loading...</span>
        </div>
        <h6 className="mt-3 text-muted fw-semibold">Fetching sawn size details...</h6>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="d-flex flex-column align-items-center justify-content-center bg-light p-4" style={{ height: "100vh", width: "100vw" }}>
        <div className="card shadow border-0 p-4 text-center" style={{ maxWidth: "450px" }}>
          <div className="text-danger mb-3">
            <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" fill="currentColor" className="bi bi-exclamation-triangle" viewBox="0 0 16 16">
              <path d="M7.938 2.016A.13.13 0 0 1 8.002 2a.13.13 0 0 1 .063.016.146.146 0 0 1 .054.057l6.857 11.667c.036.06.035.124.002.183a.163.163 0 0 1-.054.06.116.116 0 0 1-.066.017H1.146a.115.115 0 0 1-.066-.017.163.163 0 0 1-.054-.06.176.176 0 0 1 .002-.183L7.884 2.073a.147.147 0 0 1 .054-.057zm1.044-.45a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566z"/>
              <path d="M7.002 12a1 1 0 1 1 2 0 1 1 0 0 1-2 0zM7.1 5.995a.905.905 0 1 1 1.8 0l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995z"/>
            </svg>
          </div>
          <h5 className="fw-bold mb-2">Failed to load report</h5>
          <p className="text-muted small mb-4">{error?.message || "An unexpected error occurred while fetching the record details."}</p>
          <button className="btn btn-secondary w-100 fw-semibold" onClick={() => navigate(-1)}>
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{`
        .header-section {
          border-bottom: 2px solid black;
          padding-bottom: 5px;
          margin-bottom: 10px;
        }
        .pkl-download-heading {
          font-family: Cambria;
          font-size: 20px;
          font-weight: bold;
          margin: 0px;
          color: #000;
        }
        .pkl-download-from-address {
          font-family: Cambria;
          font-size: 13.5px;
          font-style: italic;
          margin: 2px 0px;
          line-height: 1.2;
          font-weight: 500;
          color: #000;
          white-space: normal;
          word-wrap: break-word;
          overflow-wrap: break-word;
          max-width: 320px;
        }
        .pkl-download-address {
          font-family: Cambria;
          font-size: 13px;
          border-bottom: 1.5px solid black;
          margin: 2px 0px;
          line-height: normal;
          font-weight: 500;
          color: #000;
          width: 100%;
          display: block;
          box-sizing: border-box;
          white-space: normal;
          word-wrap: break-word;
          overflow-wrap: break-word;
        }
        .pkl-download-table-head {
          border-bottom: 1px solid black;
          border-right: 1px solid black;
          padding: 1px;
          font-family: Calibri;
          font-size: 13px;
          font-weight: bold;
          text-align: center !important;
          background-color: #fff;
          color: #000;
        }
        .pkl-download-table-data {
          border-bottom: 1px solid black;
          border-right: 1px solid black;
          padding: 1px;
          font-family: Calibri;
          font-size: 12.5px;
          font-weight: 400;
          text-align: center !important;
          color: #000;
        }
      `}</style>

      <button
        onClick={downloadPDF}
        style={{ position: "absolute", right: "10px", top: "30px", zIndex: 10 }}
        className="btn btn-success"
      >
        Download PDF
      </button>

      <div
        style={{
          width: "45px",
          height: "45px",
          cursor: "pointer",
          position: "absolute",
          top: "8px",
          left: "40px",
          zIndex: 10,
        }}
        onClick={() => {
          navigate(-1);
        }}
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" fill="none" stroke="#000" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
          <line x1="19" y1="12" x2="5" y2="12"></line>
          <polyline points="12 19 5 12 12 5"></polyline>
        </svg>
      </div>

      <div
        className="container"
        id="pdf-content"
        style={{ padding: "10px", overflow: "auto" }}
      >
        {groupedChunks &&
          groupedChunks?.map((tableGroup, groupIndex) => (
            <div
              key={groupIndex}
              style={{ pageBreakBefore: groupIndex > 0 ? "always" : "auto" }}
            >
              <Header />
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                }}
              >
                {tableGroup &&
                  tableGroup?.map((chunk, tableIndex) => {
                    const startIndex = (groupIndex * 3 + tableIndex) * 50 + 1;
                    const totalCBM = chunk
                      .reduce((total, item) => total + parseFloat(item?.cbm || 0), 0)
                      .toFixed(3);

                    return (
                      <div
                        key={tableIndex}
                        style={{
                          width: "32%",
                          marginBottom: "10px",
                          pageBreakInside: "avoid",
                        }}
                      >
                        <table
                          style={{
                            width: "100%",
                            borderCollapse: "collapse",
                            border: "1px solid black",
                          }}
                        >
                          <thead>
                            <tr>
                              <th className="pkl-download-table-head" style={{ border: "1px solid black" }}>S.NO</th>
                              <th className="pkl-download-table-head" style={{ border: "1px solid black" }}>WIDTH</th>
                              <th className="pkl-download-table-head" style={{ border: "1px solid black" }}>THICKNS</th>
                              <th className="pkl-download-table-head" style={{ border: "1px solid black" }}>LENGTH</th>
                              <th className="pkl-download-table-head" style={{ border: "1px solid black" }}>PCS</th>
                              <th className="pkl-download-table-head" style={{ border: "1px solid black" }}>CBM</th>
                            </tr>
                          </thead>
                          <tbody>
                            {chunk?.map((data, index) => (
                              <tr key={index}>
                                <td className="pkl-download-table-data" style={{ border: "1px solid black" }}>
                                  {startIndex + index}
                                </td>
                                <td className="pkl-download-table-data" style={{ border: "1px solid black" }}>
                                  {data?.width}
                                </td>
                                <td className="pkl-download-table-data" style={{ border: "1px solid black" }}>
                                  {data?.thickness}
                                </td>
                                <td className="pkl-download-table-data" style={{ border: "1px solid black" }}>
                                  {data?.length}
                                </td>
                                <td className="pkl-download-table-data" style={{ border: "1px solid black" }}>
                                  {data?.noOfPieces}
                                </td>
                                <td className="pkl-download-table-data" style={{ border: "1px solid black" }}>
                                  {data?.cbm}
                                </td>
                              </tr>
                            ))}
                            <tr>
                              <td
                                colSpan="5"
                                className="pkl-download-table-data"
                                style={{ border: "1px solid black", fontWeight: "bold" }}
                              >
                                Total
                              </td>
                              <td className="pkl-download-table-data" style={{ border: "1px solid black", fontWeight: "bold" }}>
                                {totalCBM}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    );
                  })}
              </div>
            </div>
          ))}

        <div
          id="summary-section"
          style={{ pageBreakBefore: summaryPageBreak ? "always" : "auto", marginTop: "20px" }}
        >
          {summaryPageBreak && <Header />}
          <table className="my-3" style={{ borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th colSpan="2" style={{ padding: "4px", border: "1px solid black", backgroundColor: "#f2f2f2", fontSize: "14px" }}>
                  SUMMARY DETAILS
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td
                  style={{
                    padding: "6px 12px",
                    fontSize: "14px",
                    border: "1px solid black",
                    width: "150px"
                  }}
                >
                  <b>Total Nos</b>
                </td>
                <td
                  style={{
                    padding: "6px 12px",
                    fontSize: "14px",
                    border: "1px solid black"
                  }}
                >
                  {state?.totalTeakNos || state?.totalNos || "-"}
                </td>
              </tr>
              <tr>
                <td
                  style={{
                    padding: "6px 12px",
                    fontSize: "14px",
                    border: "1px solid black"
                  }}
                >
                  <b>Total Cbm</b>
                </td>
                <td
                  style={{
                    padding: "6px 12px",
                    fontSize: "14px",
                    border: "1px solid black"
                  }}
                >
                  {state?.totalTeakCbm || state?.totalCbm || "-"}
                </td>
              </tr>
            </tbody>
          </table>

          {/* Table for aggregated sizes */}
          {state?.aggregatedSizes?.length > 0 && (
            <table className="my-2" style={{ borderCollapse: "collapse", width: "100%", maxWidth: "600px" }}>
              <thead>
                <tr>
                  <th
                    style={{
                      border: "1px solid black",
                      padding: "6px 12px",
                      fontSize: "12px",
                      backgroundColor: "#f2f2f2",
                      fontWeight: "bold",
                      textAlign: "center"
                    }}
                  >
                    Sizes
                  </th>
                  <th
                    style={{
                      border: "1px solid black",
                      padding: "6px 12px",
                      fontSize: "12px",
                      backgroundColor: "#f2f2f2",
                      fontWeight: "bold",
                      textAlign: "center"
                    }}
                  >
                    Nos
                  </th>
                  <th
                    style={{
                      border: "1px solid black",
                      padding: "6px 12px",
                      fontSize: "12px",
                      backgroundColor: "#f2f2f2",
                      fontWeight: "bold",
                      textAlign: "center"
                    }}
                  >
                    CBM
                  </th>
                </tr>
              </thead>
              <tbody>
                {state?.aggregatedSizes?.map((data, index) => (
                  <tr key={index}>
                    <td
                      style={{
                        border: "1px solid black",
                        padding: "6px 12px",
                        fontSize: "12px",
                        textAlign: "center"
                      }}
                    >
                      {data?.sizes}
                    </td>
                    <td
                      style={{
                        border: "1px solid black",
                        padding: "6px 12px",
                        fontSize: "12px",
                        textAlign: "center"
                      }}
                    >
                      {data?.nos}
                    </td>
                    <td
                      style={{
                        border: "1px solid black",
                        padding: "6px 12px",
                        fontSize: "12px",
                        textAlign: "center"
                      }}
                    >
                      {data?.cbm != null ? Number(data.cbm).toFixed(3) : "-"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  );
};

export default SwanDownload;
